import { ChevronDown } from "lucide-react";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";

import { AssetInfo } from "@/app/asset/types";
import { Prices } from "@/app/market/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { upsert } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { pricesSocket } from "@/services/api/sockets";

import SearchCoin from "./coinInput";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

interface Props {
  coin?: AssetInfo;
}

interface AssetDetails {
  name: string | undefined;
  price: number;
}

export function CoinConverter({ coin }: Props) {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [firstCoin, setFirstCoin] = useState<AssetDetails>();
  const [showSecondDropdown, setShowSecondDropdown] = useState<boolean>(false);
  const [secondCoin, setSecondCoin] = useState<AssetDetails>();
  const [firstInput, setFirstInput] = useState<string>("");
  const [secondInput, setSecondInput] = useState<string>("");
  const [prices, setPrices] = useState<Prices[]>([]);

  useEffect(() => {
    if (coin?.asset_details) {
      setFirstCoin({
        name: coin?.asset_details.symbol ?? undefined,
        price: coin?.asset_details.current_price ?? 0,
      });
    }
  }, [coin?.asset_details]);

  useEffect(() => {
    const symbols = [
      "ETHUSDT",
      "BNBUSDT",
      "DOGEUSDT",
      "XRPUSDT",
      "BTCUSDT",
      "ADAUSDT",
    ];
    const ws = pricesSocket();
    let isSubscribed = false;

    ws.onopen = () => {
      if (!isSubscribed) {
        ws.send(
          JSON.stringify({
            subscribe: symbols,
          }),
        );
        isSubscribed = true;
      }
    };

    ws.onmessage = (event) => {
      const json = JSON.parse(event.data);
      if (json.provider != null) {
        setPrices((st) => {
          const arr = [...st];
          upsert(arr, json);

          const receivedAll = symbols.every((symbol) =>
            arr.some((price) => price.symbol === symbol),
          );

          if (receivedAll) {
            ws.close();
          }

          return arr;
        });
      }
    };

    return () => {
      ws.close();
    };
  }, []);

  const convertValues = useCallback(
    (amount: number, currentPrice1: number, currentPrice2: number) => {
      return amount * (currentPrice1 / currentPrice2);
    },
    [],
  );

  const onChangeFirstInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = Math.abs(parseFloat(e.target.value)).toString();
      setFirstInput(value);

      if (value === "") {
        setSecondInput("");
      } else {
        if (firstCoin?.name === secondCoin?.name) {
          setSecondInput(value);
        } else {
          setSecondInput(
            convertValues(
              parseFloat(value),
              firstCoin?.price ?? 1,
              secondCoin?.price ?? 1,
            ).toString(),
          );
        }
      }
    },
    [convertValues, firstCoin, secondCoin],
  );

  const onChangeSecondInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = Math.abs(parseFloat(e.target.value)).toString();
      setSecondInput(value);

      if (value === "") {
        setFirstInput("");
      } else {
        if (firstCoin?.name === secondCoin?.name) {
          setFirstInput(value);
        } else {
          setFirstInput(
            convertValues(
              parseFloat(value),
              secondCoin?.price ?? 1,
              firstCoin?.price ?? 1,
            ).toString(),
          );
        }
      }
    },
    [convertValues, firstCoin, secondCoin],
  );

  const defaultCoins = useCallback(
    (symbol: string) => {
      const coin = prices.find((e) =>
        e.symbol.toLowerCase().includes(symbol.toLowerCase()),
      );
      setSecondCoin({
        name: symbol,
        price: coin?.price ?? 0,
      });

      if (coin?.symbol.includes(firstCoin?.name?.toUpperCase() ?? "")) {
        setSecondInput(firstInput);
      } else {
        setSecondInput(
          convertValues(
            parseFloat(firstInput),
            firstCoin?.price ?? 1,
            coin?.price ?? 0,
          ).toString(),
        );
      }
    },
    [convertValues, firstCoin?.name, firstCoin?.price, firstInput, prices],
  );

  return (
    <Card className="w-full">
      <CardHeader className="mb-4">
        <CardTitle>{m.cozy_slow_spider_animate()}</CardTitle>
      </CardHeader>
      <CardContent className="gap-4 grid">
        <div className="flex items-center w-full border rounded-sm overflow-hidden">
          <DropdownMenu open={showDropdown} onOpenChange={setShowDropdown}>
            <DropdownMenuTrigger asChild disabled={coin != null}>
              <Button
                variant="secondary"
                className="rounded-sm shadow-none border-0 w-24"
              >
                <ChevronDown className="w-4 h-4 mr-2" />
                {coin?.asset_details.symbol.toUpperCase() ??
                  firstCoin?.name?.toUpperCase() ??
                  "USDT"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[300px] max-h-[400px] overflow-auto"
            >
              <SearchCoin
                onSelectCoin={(coin) => {
                  setFirstCoin({
                    name: coin.asset_data.symbol,
                    price: coin.asset_data.current_price,
                  });

                  if (coin?.asset_data.symbol === secondCoin?.name) {
                    setSecondInput(firstInput);
                  } else {
                    setSecondInput(
                      convertValues(
                        parseFloat(firstInput),
                        coin?.asset_data.current_price ?? 0,
                        secondCoin?.price ?? 1,
                      ).toString(),
                    );
                  }

                  setShowDropdown(false);
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>
          <Input
            type="number"
            placeholder="0"
            min={0}
            className="border-0 shadow-none flex-1 focus-visible:ring-0"
            value={firstInput}
            onChange={onChangeFirstInput}
          />
        </div>
        <div className="flex items-center w-full border rounded-sm overflow-hidden">
          <DropdownMenu
            open={showSecondDropdown}
            onOpenChange={setShowSecondDropdown}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                className="rounded-sm shadow-none border-0 w-24"
              >
                <ChevronDown className="w-4 h-4 mr-2" />
                {secondCoin?.name?.toUpperCase() ?? "USDT"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-[300px] max-h-[400px] overflow-auto"
            >
              <SearchCoin
                onSelectCoin={(coin) => {
                  setSecondCoin({
                    name: coin.asset_data.symbol,
                    price: coin.asset_data.current_price,
                  });

                  if (coin?.asset_data.symbol === firstCoin?.name) {
                    setSecondInput(firstInput);
                  } else {
                    setSecondInput(
                      convertValues(
                        parseFloat(firstInput),
                        firstCoin?.price ?? 1,
                        coin?.asset_data.current_price ?? 0,
                      ).toString(),
                    );
                  }

                  setShowSecondDropdown(false);
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>
          <Input
            type="number"
            placeholder="0"
            min={0}
            className="border-0 shadow-none flex-1 focus-visible:ring-0"
            value={secondInput}
            onChange={onChangeSecondInput}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Button
            variant="secondary"
            disabled={
              prices.find((e) => e.symbol.toLowerCase().includes("doge"))
                ?.symbol == null
            }
            onClick={() => defaultCoins("Doge")}
          >
            {`${coin?.asset_details.symbol.toUpperCase() ?? ""} -> Doge`}
          </Button>
          <Button
            variant="secondary"
            disabled={
              prices.find((e) => e.symbol.toLowerCase().includes("xrp"))
                ?.symbol == null
            }
            onClick={() => defaultCoins("XRP")}
          >
            {`${coin?.asset_details.symbol.toUpperCase() ?? ""} -> XRP`}
          </Button>
          <Button
            variant="secondary"
            disabled={
              prices.find((e) => e.symbol.toLowerCase().includes("btc"))
                ?.symbol == null
            }
            onClick={() => defaultCoins("BTC")}
          >
            {`${coin?.asset_details.symbol.toUpperCase() ?? ""} -> BTC`}
          </Button>
          <Button
            variant="secondary"
            disabled={
              prices.find((e) => e.symbol.toLowerCase().includes("bnb"))
                ?.symbol == null
            }
            onClick={() => defaultCoins("BNB")}
          >
            {`${coin?.asset_details.symbol.toUpperCase() ?? ""} -> BNB`}
          </Button>
          <Button
            variant="secondary"
            disabled={
              prices.find((e) => e.symbol.toLowerCase().includes("ada"))
                ?.symbol == null
            }
            onClick={() => defaultCoins("ADA")}
          >
            {`${coin?.asset_details.symbol.toUpperCase() ?? ""} -> ADA`}
          </Button>
          <Button
            variant="secondary"
            disabled={
              prices.find((e) => e.symbol.toLowerCase().includes("eth"))
                ?.symbol == null
            }
            onClick={() => defaultCoins("ETH")}
          >
            {`${coin?.asset_details.symbol.toUpperCase() ?? ""} -> ETH`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
